/**
 * useVersionCheck Hook
 * 
 * React hook wrapper around the versionCheck service.
 * Automatically checks for new version on mount (force check)
 * and provides reactive state updates.
 */

import { useState, useEffect, useCallback } from 'react';
import { versionCheck, VersionCheckResult, ServerVersionInfo } from '../../../../utils/versionCheck';

interface UseVersionCheckReturn {
  isNewVersionAvailable: boolean;
  clientVersion: string | undefined;
  serverVersion: ServerVersionInfo | null;
  lastChecked: Date | null;
  isChecking: boolean;
  error: string | null;
  checkNow: () => Promise<void>;
  refresh: () => void;
}

export const useVersionCheck = (forceCheckOnMount = true): UseVersionCheckReturn => {
  const [state, setState] = useState<VersionCheckResult>(versionCheck.getState);
  const [isChecking, setIsChecking] = useState(false);

  // Perform a version check
  const checkNow = useCallback(async () => {
    setIsChecking(true);
    try {
      const result = await versionCheck.check(true);
      setState(result);
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Force check on mount (when health screen opens)
  useEffect(() => {
    if (forceCheckOnMount) {
      setIsChecking(true);
      versionCheck.check(true).then((result) => {
        setState(result);
        setIsChecking(false);
      });
    }
  }, [forceCheckOnMount]);

  // Refresh state from service (without fetching)
  const refresh = useCallback(() => {
    setState(versionCheck.getState());
  }, []);

  return {
    isNewVersionAvailable: state.isNewVersionAvailable,
    clientVersion: state.clientVersion,
    serverVersion: state.serverVersion,
    lastChecked: state.lastChecked,
    isChecking,
    error: state.error,
    checkNow,
    refresh
  };
};

export default useVersionCheck;
